/**
 * RFQ List Page (Solicitudes de Cotización)
 *
 * Lists price-less quote requests (RFQs) with search, project/status/date
 * filters, a card/table view toggle (Issue #29) and per-row kebab actions
 * (Ver / Descargar PDF / Editar / Eliminar). "Descargar PDF" fetches the full
 * RFQ (header + items + project→client) and renders the branded PDF.
 *
 * @module pages/rfq/RfqListPage
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Dropdown,
  Collapse,
} from 'react-bootstrap';
import {
  listRfqs,
  getRfq,
  deleteRfq,
  RFQ_STATUS_LABELS,
  RFQ_STATUS_VARIANTS,
} from '../../services/rfqService';
import { listProjects } from '../../services/projectService';
import { exportRfqToPDF } from '../../utils/rfqExport';
import { toast } from '../../utils/toast';
import { formatDate } from '../../utils/formatters';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import KebabMenu from '../../components/common/KebabMenu';
import DashboardLayout from '../../components/layout/DashboardLayout';

const itemCount = (rfq) => {
  // listRfqs selects items:rfq_items(count) → [{ count: N }]
  if (Array.isArray(rfq.items)) return rfq.items[0]?.count ?? 0;
  return 0;
};

const RfqListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { viewMode, setViewMode, isTableView } = useViewToggle();

  const [filters, setFilters] = useState({
    search: '',
    projectId: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  });

  const [searchInput, setSearchInput] = useState('');
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const searchTimeoutRef = useRef(null);

  // Debounce the search input -> filters.search
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: value }));
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  // Projects for the filter dropdown
  const { data: projectsResponse } = useQuery({
    queryKey: ['rfq-projects-filter'],
    queryFn: () => listProjects({ pageSize: 500 }),
    staleTime: 1000 * 60 * 5,
  });
  const projects = projectsResponse?.data || [];

  // Fetch RFQs
  const {
    data: rfqsResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['rfqs', filters],
    queryFn: () => listRfqs(filters),
    staleTime: 1000 * 60 * 2,
    placeholderData: (prev) => prev,
  });

  const rfqs = rfqsResponse?.data || [];

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteRfq,
    onSuccess: (res) => {
      if (res?.error) {
        toast.error('Error al eliminar: ' + (res.error.message || ''));
        return;
      }
      toast.success('Solicitud eliminada');
      queryClient.invalidateQueries({ queryKey: ['rfqs'] });
      setShowDeleteModal(false);
      setSelectedRfq(null);
    },
    onError: (err) => {
      toast.error('Error al eliminar solicitud: ' + err.message);
    },
  });

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.projectId) count++;
    if (filters.status) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    return count;
  }, [filters.projectId, filters.status, filters.dateFrom, filters.dateTo]);

  const hasActiveFilters = activeFiltersCount > 0 || !!filters.search;

  const handleClearFilters = () => {
    setFilters({
      search: '',
      projectId: '',
      status: '',
      dateFrom: '',
      dateTo: '',
    });
    setSearchInput('');
  };

  const handleNew = () => navigate('/solicitudes-cotizacion/nueva');
  const handleView = (rfq) => navigate(`/solicitudes-cotizacion/${rfq.id}`);
  const handleEdit = (rfq) => navigate(`/solicitudes-cotizacion/${rfq.id}/editar`);

  const handleDownload = async (rfq) => {
    setDownloadingId(rfq.id);
    try {
      const { data, error: getError } = await getRfq(rfq.id);
      if (getError || !data) {
        throw new Error(getError?.message || 'No se pudo cargar la solicitud');
      }
      await exportRfqToPDF(data);
    } catch (err) {
      console.error('Download RFQ PDF error:', err);
      toast.error('Error al generar el PDF: ' + err.message);
    } finally {
      setDownloadingId(null);
    }
  };

  const askDelete = (rfq) => {
    setSelectedRfq(rfq);
    setShowDeleteModal(true);
  };

  const renderStatusBadge = (status) => (
    <Badge bg={RFQ_STATUS_VARIANTS[status] || 'secondary'}>
      {RFQ_STATUS_LABELS[status] || status}
    </Badge>
  );

  const renderKebab = (rfq) => (
    <KebabMenu label={`Acciones de la solicitud ${rfq.rfq_number || 'sin número'}`}>
      <Dropdown.Item onClick={() => handleView(rfq)}>
        <i className="bi bi-eye me-2"></i>
        Ver Detalle
      </Dropdown.Item>
      <Dropdown.Item
        onClick={() => handleDownload(rfq)}
        disabled={downloadingId === rfq.id}
      >
        <i className="bi bi-file-earmark-arrow-down me-2"></i>
        Descargar PDF
      </Dropdown.Item>
      <Dropdown.Item onClick={() => handleEdit(rfq)}>
        <i className="bi bi-pencil me-2"></i>
        Editar
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item className="text-danger" onClick={() => askDelete(rfq)}>
        <i className="bi bi-trash me-2"></i>
        Eliminar
      </Dropdown.Item>
    </KebabMenu>
  );

  return (
    <DashboardLayout>
    <Container fluid className="rfq-list">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <h2 className="mb-1">
                <i className="bi bi-file-earmark-ruled me-2"></i>
                Solicitudes de Cotización
              </h2>
              <p className="text-muted mb-0">
                Genera y descarga solicitudes de cotización (sin precios) para las ferreterías
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={handleNew}
                className="d-none d-md-inline-flex align-items-center"
              >
                <i className="bi bi-plus-lg me-2"></i>
                Nueva Solicitud
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Search & Filters */}
      <div className="mb-4">
        <div className="d-flex gap-2 align-items-stretch">
          <InputGroup className="flex-grow-1">
            <InputGroup.Text>
              {isFetching && !isLoading ? (
                <Spinner animation="border" size="sm" className="text-muted" />
              ) : (
                <i className="bi bi-search"></i>
              )}
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar por número o título..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              aria-label="Buscar solicitudes de cotización"
            />
          </InputGroup>
          <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          <Button
            variant={activeFiltersCount > 0 ? 'primary' : 'outline-secondary'}
            onClick={() => setIsFiltersExpanded((p) => !p)}
            aria-expanded={isFiltersExpanded}
            aria-controls="rfq-advanced-filters"
          >
            <i className={`bi bi-chevron-${isFiltersExpanded ? 'up' : 'down'}`}></i>
            <span className="d-none d-md-inline ms-1">Filtros</span>
            {activeFiltersCount > 0 && (
              <Badge bg="light" text="dark" pill className="ms-1">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </div>

        <Collapse in={isFiltersExpanded}>
          <div id="rfq-advanced-filters">
            <Card className="mt-3 border-0 shadow-sm">
              <Card.Body>
                <Row className="g-3">
                  <Col xs={12} sm={6} lg={3}>
                    <Form.Label className="small text-muted mb-1">Proyecto</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.projectId}
                      onChange={(e) => handleFilterChange('projectId', e.target.value)}
                    >
                      <option value="">Todos los proyectos</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.project_number ? `${p.project_number} — ` : ''}
                          {p.title || '(Sin título)'}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} sm={6} lg={3}>
                    <Form.Label className="small text-muted mb-1">Estado</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="">Todos</option>
                      {Object.entries(RFQ_STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={6} sm={6} lg={3}>
                    <Form.Label className="small text-muted mb-1">Desde</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    />
                  </Col>
                  <Col xs={6} sm={6} lg={3}>
                    <Form.Label className="small text-muted mb-1">Hasta</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    />
                  </Col>
                </Row>
                {hasActiveFilters && (
                  <div className="d-flex justify-content-end mt-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleClearFilters}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Limpiar filtros
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </Collapse>
      </div>

      {/* Results */}
      <Card className="shadow-sm">
        <Card.Body>
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Cargando solicitudes...</p>
            </div>
          ) : error ? (
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Error al cargar solicitudes: {error.message}
              <Button variant="link" onClick={() => refetch()}>
                Reintentar
              </Button>
            </Alert>
          ) : rfqs.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-file-earmark-ruled display-4 text-muted"></i>
              <p className="mt-3 text-muted">
                {hasActiveFilters
                  ? 'No se encontraron solicitudes con los filtros aplicados'
                  : 'No hay solicitudes de cotización registradas'}
              </p>
              <Button variant="primary" onClick={handleNew}>
                <i className="bi bi-plus-lg me-2"></i>
                Crear primera solicitud
              </Button>
            </div>
          ) : isTableView ? (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Número</th>
                    <th>Proyecto</th>
                    <th>Estado</th>
                    <th className="text-center">Ítems</th>
                    <th>Fecha</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {rfqs.map((rfq) => (
                    <tr
                      key={rfq.id}
                      onClick={() => handleView(rfq)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="fw-semibold">
                          {rfq.rfq_number || <span className="text-muted">(Sin número)</span>}
                        </div>
                        {rfq.title && <small className="text-muted">{rfq.title}</small>}
                      </td>
                      <td>
                        {rfq.project?.project_number || (
                          <span className="text-muted">Sin proyecto</span>
                        )}
                        {(rfq.project?.client?.company_name || rfq.client?.company_name) && (
                          <small className="d-block text-muted">
                            {rfq.project?.client?.company_name || rfq.client?.company_name}
                          </small>
                        )}
                      </td>
                      <td>{renderStatusBadge(rfq.status)}</td>
                      <td className="text-center">{itemCount(rfq)}</td>
                      <td>{formatDate(rfq.created_at)}</td>
                      <td className="text-end">{renderKebab(rfq)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Row className="g-3">
              {rfqs.map((rfq) => (
                <Col xs={12} sm={6} lg={4} key={rfq.id}>
                  <Card
                    className="h-100 shadow-sm rfq-card"
                    onClick={() => handleView(rfq)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            <i className="bi bi-file-earmark-ruled me-2 text-primary"></i>
                            {rfq.rfq_number || '(Sin número)'}
                          </h6>
                          <small className="text-muted">{formatDate(rfq.created_at)}</small>
                        </div>
                        {renderKebab(rfq)}
                      </div>
                      <div className="mb-2">{renderStatusBadge(rfq.status)}</div>
                      {rfq.title && (
                        <div className="small mb-1 fw-semibold">{rfq.title}</div>
                      )}
                      <div className="small text-muted mb-1">
                        <i className="bi bi-folder me-2"></i>
                        {rfq.project?.project_number || 'Sin proyecto'}
                      </div>
                      {(rfq.project?.client?.company_name || rfq.client?.company_name) && (
                        <div className="small text-muted mb-1">
                          <i className="bi bi-building me-2"></i>
                          {rfq.project?.client?.company_name || rfq.client?.company_name}
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                        <span className="text-muted small">Ítems</span>
                        <span className="fw-bold">{itemCount(rfq)}</span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Mobile FAB */}
      <Button
        variant="primary"
        className="d-md-none position-fixed shadow"
        onClick={handleNew}
        style={{
          bottom: '20px',
          right: '20px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          padding: 0,
          zIndex: 1030,
        }}
        aria-label="Nueva Solicitud"
      >
        <i className="bi bi-plus-lg fs-4"></i>
      </Button>

      {/* Delete Confirmation */}
      <ConfirmDialog
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={() => selectedRfq && deleteMutation.mutateAsync(selectedRfq.id)}
        title="Confirmar eliminación"
        message={
          selectedRfq
            ? `¿Seguro que deseas eliminar la solicitud ${selectedRfq.rfq_number || '(sin número)'}? Esta acción no se puede deshacer.`
            : ''
        }
        variant="danger"
        confirmText="Eliminar"
        cancelText="Cancelar"
      />
    </Container>
    </DashboardLayout>
  );
};

export default RfqListPage;
